import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { WifiOff, RefreshCw, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenantTheme } from '../contexts/TenantThemeContext';
import api, { authRedirectPath } from '../services/api';
import { getStoredSessionToken } from '../services/sessionTokens';
import { invalidatePlayerLobby, selectPlayerSession } from '../services/playerLobbySession';
import { isPlayerLobbyPath, PLAYER_CONNECTION_LOST } from '../services/playerConnection';
import PlayerSectionSkeleton from '../pages/player/components/PlayerSectionSkeleton';
import './player-connection.css';
import { requestPlayerAppClose } from '../lib/exitPlayerApp';

export function PlayerConnectionSkeleton({ brandName = 'Your lobby' }) {
  return <main className="player-connection-screen" data-testid="player-connection-checking" aria-busy="true">
    <div className="player-connection-body"><h1>Reconnecting</h1><p>Checking your connection and refreshing {brandName}.</p>
      <PlayerSectionSkeleton label="Refreshing lobby content" rows={4} /></div>
  </main>;
}

export default function PlayerConnectionGate({ children }) {
  const { user, checkAuth, logout } = useAuth();
  const { theme } = useTenantTheme();
  const location = useLocation(), navigate = useNavigate();
  const active = isPlayerLobbyPath(location.pathname);
  const [phase, setPhase] = useState(() => navigator.onLine === false ? 'offline' : 'ready');
  const [logoFailed, setLogoFailed] = useState(false);
  const phaseRef = useRef(phase); phaseRef.current = phase;
  const busy = useRef(false), version = useRef(0), alive = useRef(true);
  const brandName = theme?.brand_name || user?.tenant_name || 'Your lobby';
  const options = (token) => ({ timeout: 8000, ignorePlayerConnection: true, expectedSessionToken: token,
    params: { _connectionCheck: Date.now() }, headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } });
  const retryRef = useRef(null);
  const retry = async () => {
    if (busy.current || !active) return;
    busy.current = true; const request = ++version.current;
    const token = getStoredSessionToken();
    const current = () => alive.current && request === version.current && token === getStoredSessionToken();
    setPhase('checking');
    try {
      const health = await api.get('/api/health', options(token));
      if (health.data?.status !== 'healthy') throw new Error('Service unavailable');
      if (!current()) return;
      const auth = await checkAuth(options(token));
      if (!alive.current || request !== version.current) return;
      if (!auth?.user) {
        if (getStoredSessionToken()) throw new Error('Could not verify connection');
        navigate(authRedirectPath(location.pathname, window.location.hostname), { replace: true });
        setPhase('ready'); return;
      }
      // Read-only checks, never replay a deposit/load/redeem after a lost response.
      if (!current()) return;
      await Promise.all([api.get('/api/wallet/balance', options(token)), api.get('/api/games/accounts', options(token))]);
      if (!current()) return;
      invalidatePlayerLobby();
      setPhase('ready');
    } catch {
      if (alive.current && request === version.current) setPhase('offline');
    } finally {
      if (request === version.current) busy.current = false;
    }
  };
  retryRef.current = retry;

  useEffect(() => {
    alive.current = true;
    if (!active) { setPhase('ready'); return undefined; }
    const disconnected = () => {
      version.current += 1; busy.current = false;
      invalidatePlayerLobby(); setPhase('offline');
    };
    const connected = () => { if (phaseRef.current !== 'ready') void retryRef.current(); };
    const resume = () => { if (document.visibilityState !== 'hidden' && navigator.onLine === false) disconnected(); };
    if (navigator.onLine === false) disconnected();
    window.addEventListener('offline', disconnected); window.addEventListener('online', connected);
    window.addEventListener(PLAYER_CONNECTION_LOST, disconnected);
    window.addEventListener('pageshow', resume); document.addEventListener('visibilitychange', resume);
    return () => {
      version.current += 1; busy.current = false; alive.current = false;
      window.removeEventListener('offline', disconnected); window.removeEventListener('online', connected);
      window.removeEventListener(PLAYER_CONNECTION_LOST, disconnected);
      window.removeEventListener('pageshow', resume); document.removeEventListener('visibilitychange', resume);
    };
  }, [active, location.pathname]);

  const exit = async () => {
    version.current += 1; busy.current = false;
    selectPlayerSession('');
    // Offline logout clears this device immediately; it cannot revoke a remote
    // server session without a connection. No queued financial requests.
    await logout({ localOnly: true });
    requestPlayerAppClose();
    // Remains safe when a browser/older APK refuses to close. No document reload.
    navigate(authRedirectPath(location.pathname, window.location.hostname), { replace: true });
    setPhase('ready');
  };
  if (!active || phase === 'ready') return children;
  if (phase === 'checking') return <div className="player-connection-check"><PlayerConnectionSkeleton brandName={brandName} />
    <button className="player-connection-check-exit" onClick={exit}>Exit</button></div>;
  return <main className="player-connection-screen" data-testid="player-connection-offline">
    <div className="player-connection-body">
      <div className="player-connection-brand">{theme?.logo_url && !logoFailed
        ? <img src={theme.logo_url} alt="" onError={() => setLogoFailed(true)} /> : <ShieldCheck size={30} aria-hidden="true" />}<span>{brandName}</span></div>
      <WifiOff className="player-connection-icon" size={48} strokeWidth={1.5} aria-hidden="true" />
      <h1>Unable to connect</h1>
      <p role="alert">Please check your connection and try again.</p>
      <p className="player-connection-detail">Your lobby is paused. Connect to Wi-Fi or mobile data, then refresh to load the latest content.</p>
      <div className="player-connection-actions">
        <button onClick={exit}><LogOut size={19} aria-hidden="true" />Exit</button>
        <button onClick={retry} className="player-connection-refresh"><RefreshCw size={19} aria-hidden="true" />Refresh</button>
      </div>
    </div>
  </main>;
}
